import { onScopeDispose, ref, Ref, watch } from 'vue';
import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  Padding,
  Placement,
  shift,
} from '@floating-ui/dom';
import { assistantInset } from '@/composition/assistant/assistant-inset';

export interface IFloatingPanelOptions {
  /** Called when the anchor stops being visible (hidden by `v-show`, collapsed). */
  onAnchorHidden?: () => void;
  /** Gap between the anchor and the panel (px); 4 by default. */
  offset?: () => number;
}

/** Room kept free around panels (px). */
const VIEWPORT_PADDING = 8;

/** The viewport padding, plus the docked «Asistente» panel on the right. */
function viewportPadding(): Padding {
  return {
    top: VIEWPORT_PADDING,
    bottom: VIEWPORT_PADDING,
    left: VIEWPORT_PADDING,
    right: VIEWPORT_PADDING + assistantInset.value,
  };
}

/**
 * Keeps `panel` next to `anchor` with `position: fixed` (4 px offset, flip
 * and shift with 8 px of viewport padding, plus the docked assistant's width
 * on the right) while `active` is true, so the panel never clips inside
 * overflow containers and never leaves the page area.
 * `positioned` stays false until the first position lands: render the panel
 * invisible (but measurable) until then to avoid a flash at (0, 0).
 */
export function useFloatingPanel(
  anchor: Ref<HTMLElement | null | undefined>,
  panel: Ref<HTMLElement | null | undefined>,
  active: Ref<boolean>,
  placement: () => Placement,
  options: IFloatingPanelOptions = {}
) {
  const positioned = ref(false);
  let stopAutoUpdate: (() => void) | null = null;

  async function updatePosition(reference: HTMLElement, floating: HTMLElement) {
    if (!reference.getClientRects().length) {
      options.onAnchorHidden?.();
      return;
    }
    const padding = viewportPadding();
    const { x, y } = await computePosition(reference, floating, {
      strategy: 'fixed',
      placement: placement(),
      middleware: [
        offset(options.offset?.() ?? 4),
        flip({ padding }),
        shift({ padding }),
      ],
    });
    // The panel may have been torn down, moved to another anchor or closed
    // while computePosition awaited.
    if (panel.value !== floating || anchor.value !== reference || !active.value)
      return;
    floating.style.left = `${x}px`;
    floating.style.top = `${y}px`;
    positioned.value = true;
  }

  function stopPositioning() {
    if (stopAutoUpdate) {
      stopAutoUpdate();
      stopAutoUpdate = null;
    }
  }

  // flush: 'post' so a v-if panel exists when positioning starts.
  watch(
    [active, anchor, panel],
    () => {
      stopPositioning();
      positioned.value = false;
      const reference = anchor.value;
      const floating = panel.value;
      if (!active.value || !reference || !floating) return;
      stopAutoUpdate = autoUpdate(reference, floating, () => {
        void updatePosition(reference, floating);
      });
    },
    { flush: 'post' }
  );

  onScopeDispose(stopPositioning);

  return { positioned };
}
